import { gsap } from 'gsap';
import { LoopOnce, Vector3 } from 'three';

import useThreeAnimations from '~/composables/useThreeAnimations';
import { logger } from '~/utils/logger';

// Initialize the animation system outside the class
const { setupAnimations, updateAnimations, getAnimations, getMixer, clearAnimations } =
    useThreeAnimations();

export class RipWaxTransition {
    constructor(object, params = {}) {
        this.name = 'rip-wax';
        this.object = object;
        this.duration = params.duration;
        this.propsAnimation = params.propsAnimation;
        this.transparent = false;
        this.progress = 0;
        this.manualMode = false;

        this.tweens = { yRotPr: 0, yPr: 0 };

        const idAnim = 'Plane.002Action';

        // Setup animations from the GLTF object
        // Assuming the object has animations property from the loaded GLTF
        if (object.animations && object.animations.length > 0) {
            setupAnimations({ animations: object.animations, object });

            // Find the specific "Plane.007Action" animation
            const animations = getAnimations();
            if (animations && animations.length > 0) {
                // Look for the specific animation by name
                this.animation = animations.find((anim) => anim.name === idAnim);

                // Fallback to first animation if specific one not found
                if (!this.animation) {
                    this.animation = animations[0];
                    //console.warn(
                    //'RipWaxTransition: "' +
                    //idAnim +
                    //'" animation not found, using first available animation:',
                    //animations[0].name
                    //);
                }
                if (!this.duration) this.duration = this.animation.duration;
                // console.log('🚀 ~ RipWaxTransition ~ constructor ~ this.duration:', this.duration);
            }
        } else {
            logger.warn('RipWaxTransition: No animations found on object');
        }

        // Initialize the timeline in the constructor so it can be scrubbed independently
        this._initializeTimeline();
    }

    _initializeTimeline() {
        this.tl = gsap
            .timeline({ paused: true })
            .to(this.object.rotation, { y: Math.PI, duration: 0.8, ease: 'power2.out' }, 0)
            .to(this.object.position, { y: -4, duration: 1.5, ease: 'power2.in' }, 0.8)
            .add(() => {}, this.duration);
    }

    setManualMode(val) {
        this.manualMode = val;
    }

    setSpinRotation(rotation) {
        gsap.killTweensOf(this.object.rotation);
        gsap.to(this.object.rotation, { y: rotation, duration: 0.8, ease: 'power3.out' });
    }

    createCustomMeshes(parent) {
        this.parent = parent;
        // Add any custom meshes needed for the rip effect
    }

    setTransitionUniforms(shader) {
        // Enable any shader-based transition effects if needed
        // shader.USE_TRANSITION_RIP_WAX = true;
        // shader.needsUpdate = true;
    }

    update(progress) {
        // Don't update mixer if we're at the end to prevent snapping back
        if (progress >= 1) {
            return;
        }

        // Update the animation mixer with delta time
        // We calculate delta based on the progress change
        const delta = (progress - this.progress) * this.duration * 1000;

        if (getMixer()) updateAnimations({ delta, timestamp: performance.now() });
    }

    setProgress(progress) {
        this.progress = progress;

        // Scrub the GSAP timeline to the specified progress (0 to 1)
        if (this.tl) {
            this.tl.progress(progress);
        }

        this.setAnimationProgress(progress);
    }

    setAnimationProgress(progress) {
        // Set the GLTF animation time directly based on progress
        const mixer = getMixer();

        if (mixer && this.animation) {
            const { action, duration } = this.animation;

            // Enable the action and set its weight
            action.enabled = true;
            action.setEffectiveWeight(1);

            // Prevent looping to avoid snapping back to 0
            action.setLoop(LoopOnce);
            action.clampWhenFinished = true;

            // Set the time directly based on progress
            const clampedProgress = Math.min(Math.max(progress, 0), 1);
            const time = duration * (clampedProgress === 1 ? 1 : Math.min(clampedProgress, 0.999));

            mixer.setTime(time);

            // If at the end, pause the action to prevent any further updates
            if (clampedProgress === 1) {
                action.paused = true;
                action.time = duration;
            } else {
                if (!action.isRunning()) action.play();
                action.paused = false;
            }
        }
    }

    animateDown() {
        gsap.killTweensOf(this.object.position);
        const tl = gsap.timeline({ paused: false });
        tl.to(this.object.position, { y: -4, z: -1, duration: 0.7, ease: 'power2.in' });

        return tl;
    }

    destroy() {
        this.reset();

        // Clean up resources and remove animations from the array
        clearAnimations();

        // Clear reference to animation
        this.tl?.kill();
        this.tl = null;

        this.animation = null;
    }

    reset() {
        this.object.position.set(0, 0, 0);
        this.object.rotation.set(0, 0, 0);

        // Reset animation state
        const mixer = getMixer();
        if (mixer && this.animation) {
            const { action } = this.animation;
            action.reset();
            action.stop();
            action.setEffectiveWeight(0);
        }

        this.progress = 0;
    }
}
